import { useState } from "react";
import Link from "next/link";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import { useNowCalculatorBudget } from "@/shared/hooks/useNowCalculatorBudget";
import { formatPrice } from "@/utils/formatPrice";
import routes from "@/constants/routes";

const MIN_ANNUAL_INCOME = 30000;
const MAX_ANNUAL_INCOME = 180000;
const DEFAULT_ANNUAL_INCOME = 70000;

const Calculator = () => {
  const [annualIncome, setAnnualIncome] = useState<number>(DEFAULT_ANNUAL_INCOME);
  const [keyzyBudget] = useNowCalculatorBudget(annualIncome);

  return (
    <section id="calculator" className="bg-landing-gray py-14 pb-6 md:py-20">
      <div className="px-5 lg:px-20 mx-auto max-w-7xl">
        <div className="flex flex-col md:flex-row gap-10 md:gap-12 items-center">
          <div>
            <h2 className="text-3xl md:text-[2.5rem] leading-tight font-bold text-landing-blue">
              Rent your future home and{" "}
              <span className="text-landing-cherry">get 100% of your rent back</span> to use as a
              deposit
            </h2>
            <p className="mt-5 text-base text-landing-blue max-w-[40ch]">
              With Keyzy, enjoy fixed rent for 2 years and the option to buy your home at a
              guaranteed price, giving you the freedom to buy when you&apos;re ready — all while
              building your deposit from day one.
            </p>
            <Link href={routes.searchHomes}>
              <a className="inline-block mt-7 px-6 py-3 text-base font-bold text-center text-white transition duration-300 ease-in-out transform rounded shadow bg-landing-cherry  hover:scale-105">
                Browse homes
              </a>
            </Link>
          </div>

          <div className="bg-white rounded-[20px] p-6 md:p-8 shadow-xl">
            <span className="text-base leading-3 font-bold tracking-[0.96px] uppercase text-grey-500">
              Annual household income
            </span>
            <div className="flex items-baseline gap-2 mt-3 mb-5">
              <span className="text-4xl font-bold text-landing-blue tabular-nums">
                {formatPrice(annualIncome)}
              </span>
              <span className="text-sm text-grey-500">/ year</span>
            </div>

            <Slider
              min={MIN_ANNUAL_INCOME}
              max={MAX_ANNUAL_INCOME}
              step={1000}
              value={annualIncome}
              onChange={(v) => setAnnualIncome(v as number)}
              trackStyle={{ backgroundColor: "#E7E6EF", height: 16, borderRadius: 30 }}
              handleStyle={{
                borderColor: "#FFFFFF",
                height: 28,
                width: 28,
                marginTop: -6,
                borderWidth: 4,
                backgroundColor: "#F39237",
                boxShadow: "0 2px 8px 0 rgba(0, 0, 0, 0.25)",
              }}
              railStyle={{
                backgroundColor: "#E7E6EF",
                height: 16,
                borderRadius: 30,
                boxShadow: "0 4px 56px 0 rgba(61, 75, 91, 0.08)",
              }}
            />
            <div className="flex justify-between text-xs font-semibold text-grey-400 mt-5">
              <span>{formatPrice(MIN_ANNUAL_INCOME).split(",")[0]}k</span>
              <span>{formatPrice(MAX_ANNUAL_INCOME).split(",")[0]}k</span>
            </div>

            <div className="grid grid-cols-2 md:flex md:flex-row gap-3.5 mt-7">
              <div className="rounded-[14px] bg-white shadow-[0_6px_20px_rgba(16,32,58,0.08)] p-[18px] flex flex-col gap-1.5 md:w-[264px] ">
                <span className="text-base leading-4 font-semibold text-grey-600 flex flex-col sm:flex-row sm:gap-1">
                  <span>Max monthly</span>
                  <span>rent</span>
                </span>
                <span className="text-xl sm:text-2xl md:text-[30px] font-bold text-landing-blue tabular-nums">
                  {keyzyBudget?.rent}{" "}
                  <span className="text-base font-medium text-grey-500">pcm</span>
                </span>
              </div>
              <div className="rounded-[14px] bg-[#FDEBDA] shadow-[0_6px_20px_rgba(16,32,58,0.08)] p-[18px] flex flex-col gap-1.5 md:w-[264px]">
                <span className="text-base leading-4 font-semibold text-grey-600 flex flex-col sm:flex-row sm:gap-1">
                  <span>Deposit you&apos;d</span>
                  <span>build (2 yrs)</span>
                </span>
                <span className="text-xl sm:text-2xl md:text-[30px] font-bold text-landing-blue tabular-nums">
                  {keyzyBudget?.deposit}
                </span>
              </div>
            </div>

            <p className="text-base text-grey-600 mt-5">
              That&apos;s 100% of your rent, refunded when you buy — putting you on track for a home
              buying budget of around <b>{keyzyBudget?.indicativeBudget}</b>. Figures are
              illustrative and subject to affordability checks.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Calculator;
